import { ChangeEvent, FC, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { SingleOptTimeRangePicker } from "components/date/SingleOptTimePeriodPicker"
import { WelcomeSection } from "components/general/WelcomeSection"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { TagsMultiSelect } from "components/inputs/TagsMultiSelect"
import { TopBar } from "components/navigation/TopBar"
import { SearchInput } from "components/search/SearchInput"
import { Table } from "components/tables/Table"

import { ModalCreateUser } from "features/user/actions/ModalCreateUser/ModalCreateUser"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { URL } from "constants/api/requests"
import { countries } from "constants/countries"
import { tableUsersColumns } from "constants/data"
import { PERMISSIONS } from "constants/permissions"

import { tagsService } from "services/tags/tags.service"
import { userService } from "services/user/user.service"

export const Users: FC = () => {
  const { user } = useProps()
  const { has } = usePermissions()
  const { t } = useTranslation(["translation"], { useSuspense: false })

  const [query, setQuery] = useState<string>("")
  const [tableUpdatedAt] = useState<Date | number>(Date.now())
  const [timeframe, setTimeframe] = useState<(Date | null)[]>([])
  const [country, setCountry] = useState<string | null>(null)
  const [openCreateUser, setOpenCreateUser] = useState(false)
  const [tagIds, setTagIds] = useState<string[]>([])
  const [tagOptions, setTagOptions] = useState<{ name: string; value: string }[]>([])

  const handleChangeTimeFrame = (dates: (Date | null)[]) => setTimeframe(dates)

  const onUserClick = (row: any): void => {
    if (row?.id) {
      window.open(URL.USER.replace(":id", row.id), "_blank")
    }
  }

  useEffect(() => {
    const loadTags = async () => {
      const res = await tagsService.getTags({ page: 1, pageSize: 500 })
      const opts = (res?.data ?? []).map((t) => ({ name: t.name, value: t.id }))
      setTagOptions(opts)
    }
    loadTags()
  }, [])

  return (
    <div className="grid w-full justify-start gap-8 p-4 pt-0 sm:p-8 sm:pt-0 grid-col-1">
      <TopBar title={t("pages.users.title")} />
      <WelcomeSection />
      <div className="flex gap-4 z-20 flex-wrap">
        <div className="max-w-sm">
          <SearchInput placeholder={t("form.searchUsernameEmailPhoneAddressIpUuid")} onChange={setQuery} />
        </div>

        <div className="max-w-md z-20">
          <SingleOptTimeRangePicker noLabel onTimeframeChange={handleChangeTimeFrame} />
        </div>

        <div className="text-wrap max-w-sm shrink">
          <DropdownMenu
            placeholder={t("form.allCountries")}
            options={countries}
            value={country}
            onChange={(ev: ChangeEvent<HTMLSelectElement>) => setCountry(ev.target.value)}
            rounded
          />
        </div>

        <div className="text-wrap max-w-sm shrink">
          <TagsMultiSelect placeholder={t("form.allTags")} options={tagOptions} value={tagIds} onChange={setTagIds} rounded />
        </div>
        <button
          className="ml-auto btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
          onClick={() => setOpenCreateUser(true)}
        >
          {t("common.create")}
        </button>
      </div>
      <Table
        title={t("user.users")}
        handler={userService.getUsersTable}
        columns={tableUsersColumns}
        filters={{ q: query, createdAt: timeframe, country, tagIds }}
        onRowClick={onUserClick}
        updatedAt={tableUpdatedAt}
        isExport={!!user && has(PERMISSIONS.PAGE_ADMINS)}
        isColumnsFilterable
      />
      <ModalCreateUser open={openCreateUser} handler={() => setOpenCreateUser(false)} />
    </div>
  )
}
